import { Trans, useLingui } from "@lingui/react/macro";
import { updateDescription } from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
  DescriptionInput,
  type IDescriptionInputFields,
} from "@/forms/AddActivity/DescriptionInput";

export const EditActivityDescription = ({
  currentDescription,
  onSuccess,
  activityId,
}: {
  currentDescription: string | null | undefined;
  onSuccess: () => void;
  activityId: string;
}) => {
  const { t } = useLingui();
  const methods = useForm<IDescriptionInputFields>({
    defaultValues: {
      description: currentDescription ?? "",
    },
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
  } = methods;

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (description: string) =>
      updateDescription(activityId, description),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit((formData) => {
          mutate(formData.description);
        })}
      >
        {isPending || isSuccess ? (
          <Loading message={t`Ändrar beskrivning`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <Form.Row>
              <DescriptionInput />
            </Form.Row>
            {isDirty ? (
              <FilledButton type="submit">
                <Trans>Spara ändringar</Trans>
              </FilledButton>
            ) : null}
          </>
        )}
      </Form>
    </FormProvider>
  );
};
